import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePoColumns from 'src/assets/static/new_receipt/usePoColumns';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import SearchInput from 'src/components/SearchInput';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useRedirect from 'src/hooks/useRedirect';
import useStep from 'src/hooks/useStep';
import useInvoicePoAssignmentApi, {
  InvoicePoAssignmentRequest,
} from 'src/services/useInvoicePoAssignmentApi';
import { IInvoiceDocument } from 'src/types/InvoiceDocument';
import { IPO } from 'src/types/PO';
import { hasPermission } from 'src/utils/JWT';
import { useContextSelector } from 'use-context-selector';
import DocumentGrid from '../enter_invoice/DocumentGrid';
import PoGrid from '../enter_receipt/PoGrid';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

// poMPA  invoiceMPA  permission
//  1         1           1
//  1         0           2
//  0         1           3
//  0         0           4

const MpaPermissions = [1, 2, 3];
const AssignMultiPeriodInvoicePermissions = [1, 3];
const CopyPOPeriodPermissions = [1, 2];

function getPermissionNumber(isPOMPA: boolean, isInvoiceMPA: boolean) {
  const binaryCombination = (isPOMPA ? '1' : '0') + (isInvoiceMPA ? '1' : '0');
  const index = 4 - parseInt(binaryCombination, 2);
  return index;
}

const ChangePoAssignment = () => {
  const { t } = useTranslation();
  const { company: selectedCompany } = useCompany();
  const companyId = useContextSelector(InvoiceContext, (value) => value.companyId);
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);
  const isInvoiceMPA = useContextSelector(InvoiceContext, (value) => value.isInvoiceMPA);
  const { changeInvoicePoAssignment, isLoading } = useInvoicePoAssignmentApi();
  const { getUrl, redirect } = useRedirect();
  const { status } = invoice;
  const [changePoAssignmentMessage, changePoAssignmentButtonState] =
    useInvoiceButtonState('ChangePO');
  const [currentStep, helpers] = useStep(2);
  const poColumns = usePoColumns();

  const { goToNextStep, goToPrevStep, setStep } = helpers;
  const {
    value: isAssignPoModalVisible,
    setTrue: openAssignPoModal,
    setFalse: closeAssignPoModal,
  } = useBoolean(false);
  const [searchPo, setSearchPo] = useState<{ value: string; disable: boolean }>({
    value: '',
    disable: false,
  });
  const [supplierSearch, setSupplierSearch] = useState<string>('');
  const [lineItemSearch, setLineItemSearch] = useState<string>('');
  const [selectedInvoicePoRow, setSelectedInvoicePoRow] = useState<IPO | null>(null);
  const [, setSelectedInvoiceRow] = useState<IInvoiceDocument | null>(null);

  const { isDisableNext, nextButtonMessage } = useMemo(() => {
    const isDisableNext = !selectedInvoicePoRow?.selected;
    let nextButtonMessage = '';

    if (isDisableNext) {
      nextButtonMessage = 'Please select a PO.';
    }
    return {
      isDisableNext,
      nextButtonMessage,
    };
  }, [selectedInvoicePoRow?.selected]);

  const {
    value: isMpaModalVisible,
    setTrue: openMpaModal,
    setFalse: closeMpaModal,
  } = useBoolean(false);
  const {
    value: isSinglePeriodLoading,
    setTrue: startSinglePeriodLoading,
    setFalse: endSinglePeriodLoading,
  } = useBoolean(false);
  const {
    value: isMultiPeriodLoading,
    setTrue: startMultiPeriodLoading,
    setFalse: endMultiPeriodLoading,
  } = useBoolean(false);
  const {
    value: isCopyPoPeriodLoading,
    setTrue: startCopyPoPeriodLoading,
    setFalse: endCopyPoPeriodLoading,
  } = useBoolean(false);

  const isSelectedPOMPA = useMemo(
    () =>
      selectedInvoicePoRow?.allocations ? selectedInvoicePoRow?.allocations.length > 1 : false,
    [selectedInvoicePoRow]
  );
  const mpaPermissionNumber = useMemo(
    () => getPermissionNumber(isSelectedPOMPA, isInvoiceMPA),
    [isInvoiceMPA, isSelectedPOMPA]
  );
  const hasMpaPermission = hasPermission(MpaPermissions, mpaPermissionNumber);
  const hasAssignMultiPeriodInvoicePermission = hasPermission(
    AssignMultiPeriodInvoicePermissions,
    mpaPermissionNumber
  );
  const hasCopyPOPeriodPermission = hasPermission(CopyPOPeriodPermissions, mpaPermissionNumber);

  const handleCloseModal = useCallback(() => {
    closeAssignPoModal();
    setSearchPo((prev) => ({ ...prev, value: '', disable: false }));
    setSupplierSearch('');
    setLineItemSearch('');
    setStep(1);
  }, [closeAssignPoModal, setStep]);

  const handlePoAssignment = useCallback(
    async (request?: InvoicePoAssignmentRequest) => {
      const poAssignmentRequest = {
        invoiceId: parseInt(invoiceId),
        poId: selectedInvoicePoRow?.poId,
        ...request,
      };
      await changeInvoicePoAssignment(poAssignmentRequest)
        .then((res) => {
          if (!res) return;
          redirect(getUrl('invoiceDetails')(String(res), String(selectedInvoicePoRow?.poId)));
          refreshPage();
        })
        .catch(console.error)
        .finally(() => {
          handleCloseModal();
          closeMpaModal();
        });
    },
    [
      invoiceId,
      selectedInvoicePoRow?.poId,
      changeInvoicePoAssignment,
      redirect,
      getUrl,
      refreshPage,
      handleCloseModal,
      closeMpaModal,
    ]
  );

  const handleAssignPoClick = useCallback(() => {
    if (hasMpaPermission) {
      handleCloseModal();
      openMpaModal();
    } else {
      handlePoAssignment();
    }
  }, [handlePoAssignment, hasMpaPermission, openMpaModal, handleCloseModal]);

  const handlePoAssignmentWithSinglePeriod = useCallback(async () => {
    startSinglePeriodLoading();
    await handlePoAssignment({ useSinglePeriod: true });
    endSinglePeriodLoading();
  }, [endSinglePeriodLoading, handlePoAssignment, startSinglePeriodLoading]);

  const handlePoAssignmentWithMultiInvoicePeriod = useCallback(async () => {
    startMultiPeriodLoading();
    await handlePoAssignment({ copyInvoiceAllocations: true });
    endMultiPeriodLoading();
  }, [endMultiPeriodLoading, handlePoAssignment, startMultiPeriodLoading]);

  const handlePoAssignmentWithMultiPoPeriod = useCallback(async () => {
    startCopyPoPeriodLoading();
    await handlePoAssignment({ useSinglePeriod: false });
    endCopyPoPeriodLoading();
  }, [endCopyPoPeriodLoading, handlePoAssignment, startCopyPoPeriodLoading]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      redirect(getUrl('poDetails')(String(selectedInvoicePoRow?.poId)), true, true);
    },
    [getUrl, redirect, selectedInvoicePoRow?.poId]
  );

  const nextStep = useCallback(() => {
    if (currentStep === 1 && !selectedInvoicePoRow?.selected) return;
    setSearchPo((prev) => ({ ...prev, disable: true }));
    goToNextStep();
  }, [currentStep, goToNextStep, selectedInvoicePoRow?.selected]);

  const goBack = useCallback(() => {
    setSearchPo((prev) => ({ ...prev, disable: false }));
    goToPrevStep();
  }, [goToPrevStep]);

  const handlePoSearch = useCallback(() => {
    setSearchPo((prev) => ({ ...prev, disable: true }));
    nextStep();
  }, [nextStep]);

  const AssignPoTitle = useMemo(
    () => (currentStep === 1 ? 'Search for new PO' : 'Invoices Related to PO'),
    [currentStep]
  );

  if (status.includes('Completed')) return <></>;

  return (
    <>
      <PopUp
        content={<PopUpContentWrapper type="Error" contents={[...changePoAssignmentMessage]} />}
        show={isButtonDisabled(changePoAssignmentButtonState)}
      >
        <Button
          onClick={openAssignPoModal}
          buttonState={changePoAssignmentButtonState}
          color="warning"
          disabled={isButtonDisabled(changePoAssignmentButtonState)}
        >
          Change PO Assignment
        </Button>
      </PopUp>

      <Dialog
        width="90%"
        height="90%"
        open={isAssignPoModalVisible}
        onClose={handleCloseModal}
        title={AssignPoTitle}
        id="modal"
      >
        <div>
          <div className="mx-[-16px] mt-[-16px]">
            <nav className="h-14 shadow-md flex items-center justify-center px-4">
              <div>
                {currentStep === 1 && (
                  <PopUp
                    content={<PopUpContentWrapper type="Warning" contents={[nextButtonMessage]} />}
                    show={!!nextButtonMessage}
                  >
                    <Button onClick={nextStep} disabled={isDisableNext}>
                      Next
                    </Button>
                  </PopUp>
                )}
                {currentStep === 2 && (
                  <div className="flex">
                    <Button onClick={handleAssignPoClick} className="mr-2" loading={isLoading}>
                      Apply to this PO
                    </Button>
                    <Button onClick={goBack} color="info">
                      Back
                    </Button>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div>
            {currentStep === 1 ? (
              <p className="mt-2">
                Use filters provided to locate a PO that you would like to associate this document
                with.
              </p>
            ) : (
              <p className="mt-2">
                Verify that a duplicate Invoice does not exist for this PO by reviewing records
                displayed below
              </p>
            )}
            <div className="p-3 grid grid-cols-3 gap-3">
              <SearchInput
                width="100%"
                title="PO #"
                value={searchPo.value}
                onChange={(e) => {
                  setSearchPo(() => ({ value: e.target.value, disable: false }));
                }}
                placeholder="Enter PO # (Optional)"
                disabled={searchPo.disable}
                onKeyDown={handlePoSearch}
              />
              {currentStep === 1 && (
                <>
                  <SearchInput
                    width="100%"
                    title="Filter PO's by Supplier Name"
                    placeholder="Enter supplier name (Optional)"
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                    }}
                  />

                  <SearchInput
                    width="100%"
                    title="Filter PO's by Item"
                    value={lineItemSearch}
                    placeholder="Enter item # or description (Optional)"
                    onChange={(e) => {
                      setLineItemSearch(e.target.value);
                    }}
                  />
                </>
              )}
            </div>
          </div>
          {currentStep === 1 && (
            <PoGrid
              poNumber={searchPo.value}
              setSelectedInvoicePoRow={setSelectedInvoicePoRow}
              companyId={selectedCompany.companyId || companyId}
              supplierSearch={supplierSearch}
              setSupplierSearch={setSupplierSearch}
              lineItemSearch={lineItemSearch}
              setLineItemSearch={setLineItemSearch}
              columns={poColumns}
              setPoSearch={setSearchPo}
              isPoAssigned
              isInvoice
              isDetailsPage={true}
            />
          )}
          {currentStep === 2 && (
            <DocumentGrid
              poId={selectedInvoicePoRow?.poId}
              poNumber={selectedInvoicePoRow?.poNum}
              setSelectedInvoiceRow={setSelectedInvoiceRow}
              isDetailsPage={true}
              showInvoiceSummary
            />
          )}
        </div>
        <DialogActionsBar>
          <div className="flex items-center justify-end">
            <Button color="info" onClick={handleCloseModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>

      <Dialog
        width={700}
        open={isMpaModalVisible}
        onClose={closeMpaModal}
        title="Multiple Period Allocation"
        id="modal"
      >
        {mpaPermissionNumber === 1 && (
          <p>
            {t(
              "The invoice and the PO you've selected to update the assignment for have multiple accounting periods set. Would you like the invoice to maintain the multiple periods set on the invoice, copy the periods and allocations as they are set on the PO "
            )}
            <span className="text-link cursor-pointer underline" onClick={handleClick}>
              #{selectedInvoicePoRow?.poNum}
            </span>
            {t(' or assign the invoice a single period after updating the PO assignment?')}
          </p>
        )}
        {mpaPermissionNumber === 2 && (
          <p>
            {t(
              "The PO you've selected to update the PO assignment with has multiple accounting periods set. Would you like to copy the periods and allocations as they are set on the PO "
            )}
            <span className="text-link cursor-pointer underline" onClick={handleClick}>
              #{selectedInvoicePoRow?.poNum}
            </span>
            {t(' or assign the invoice a single period after updating the PO assignment?')}
          </p>
        )}

        {mpaPermissionNumber === 3 && (
          <p>
            {t(
              "The invoice you've selected to update the PO assignment for has multiple accounting periods set. Would you like to maintain the multiple periods set on the invoice, or assign the  invoice a single period after updating the PO assignment?"
            )}
          </p>
        )}

        <DialogActionsBar>
          <div className="flex items-center justify-end">
            {hasAssignMultiPeriodInvoicePermission && (
              <Button
                color="info"
                onClick={handlePoAssignmentWithMultiInvoicePeriod}
                loading={isMultiPeriodLoading}
                disabled={isSinglePeriodLoading || isCopyPoPeriodLoading}
              >
                {t('Maintain Multiple Invoice Periods')}
              </Button>
            )}
            {hasCopyPOPeriodPermission && (
              <Button
                color="info"
                onClick={handlePoAssignmentWithMultiPoPeriod}
                loading={isCopyPoPeriodLoading}
                disabled={isMultiPeriodLoading || isSinglePeriodLoading}
              >
                {t('Copy Multiple PO Periods')}
              </Button>
            )}
            <Button
              color="info"
              onClick={handlePoAssignmentWithSinglePeriod}
              loading={isSinglePeriodLoading}
              disabled={isMultiPeriodLoading || isCopyPoPeriodLoading}
            >
              {t('Assign Single Period')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default ChangePoAssignment;
